// "Neue Aufgabe" modal — lets the user pick an Ablage (Persönlich or a Space)
// and creates the task in the appropriate list, auto-creating a default list
// named "Aufgaben" when the chosen Space has no writable list yet.

import { useEffect, useState } from "preact/hooks";
import {
  createPersonalTask,
  createList,
  fetchSpaceMembers,
  useLists,
  type SpaceMember,
} from "../store/personalTasks";
import { useSpaces } from "../store/spaces";
import { useUserPrefs } from "../store/userPrefs";
import { resolveTargetList } from "../lib/defaultList";
import { toastSuccess } from "../lib/toast";
import { Modal } from "./Modal";
import { Field, INPUT_CLS } from "./NewProjectModal";
import { AssigneePicker } from "./AssigneePicker";

import { Button } from "./ui/Button";

interface Props {
  onClose: () => void;
  onCreated: () => void;
}

type Priority = "low" | "medium" | "high" | "urgent";

/** Sentinel value meaning "Persönlich" is selected in the Ablage chooser. */
const PERSONAL = "personal";

export function NewPersonalTaskModal(props: Props): preact.JSX.Element {
  const { onClose, onCreated } = props;
  const { lists } = useLists();
  const spaces = useSpaces();
  const prefs = useUserPrefs();

  // --- form state ---
  const [title, setTitle] = useState("");
  const [ablage, setAblage] = useState<string>(PERSONAL); // PERSONAL or String(spaceId)
  const [personalListId, setPersonalListId] = useState<number | undefined>(undefined);
  const [dueDate, setDueDate] = useState("");
  const [priority, setPriority] = useState<Priority>("medium");
  // #854: mehrere Zuständige; die erste Person ist die primäre.
  const [assigneeIds, setAssigneeIds] = useState<number[]>([]);
  const [spaceMembers, setSpaceMembers] = useState<SpaceMember[]>([]);
  const [submitting, setSubmitting] = useState(false);

  // Writable spaces (editor role).
  const writableSpaces = spaces.items.filter((s) => s.my_role === "editor");

  const selectedSpaceId = ablage !== PERSONAL ? Number(ablage) : null;

  // Fetch space members whenever a space is selected.
  useEffect(() => {
    if (selectedSpaceId !== null) {
      void fetchSpaceMembers(selectedSpaceId).then((members) => {
        setSpaceMembers(members);
        setAssigneeIds([]);
      });
    } else {
      setSpaceMembers([]);
      setAssigneeIds([]);
    }
  }, [selectedSpaceId]);

  // Personal lists — scope !== "space".
  const personalLists = lists.filter(
    (l) => !l.scope || l.scope !== "space"
  );

  // Vorauswahl, sobald die Listen da sind: „Ohne Liste" gibt es nicht mehr,
  // also startet das Feld auf der Zielliste des Nutzers (Standard-Liste aus den
  // Einstellungen, sonst erste eigene Liste). Bleibt sie leer (Nutzer ohne
  // Liste), legt der Server die Aufgabe in seiner Zielliste ab.
  useEffect(() => {
    if (personalListId !== undefined) return;
    const target = resolveTargetList(lists, prefs.default_list_id);
    if (target) setPersonalListId(target.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lists, prefs.default_list_id]);

  const onSubmit = async (e: Event): Promise<void> => {
    e.preventDefault();
    const trimmed = title.trim();
    if (!trimmed) return;

    setSubmitting(true);
    try {
      if (ablage === PERSONAL) {
        await createPersonalTask({
          title: trimmed,
          list_id: personalListId ?? undefined,
          due_date: dueDate || undefined,
          priority,
        });
      } else {
        const spaceId = Number(ablage);
        // Find an existing writable shared list for this space.
        const existing = lists.find(
          (l) =>
            l.scope === "space" &&
            l.space_id === spaceId &&
            (l.role === "editor" || l.role === "owner")
        );
        let listId: number;
        if (existing) {
          listId = existing.id;
        } else {
          const created = await createList("Aufgaben", undefined, spaceId);
          listId = created.id;
        }
        await createPersonalTask({
          title: trimmed,
          list_id: listId,
          due_date: dueDate || undefined,
          priority,
          assignee_ids: assigneeIds.length > 0 ? assigneeIds : undefined,
        });
      }
      toastSuccess("Aufgabe erstellt");
      onCreated();
    } catch {
      // api.ts already surfaces a toast for the server error; just re-enable.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal title="Neue Aufgabe" onClose={onClose} size="md" dirty={title.trim() !== "" || dueDate !== ""}>
      <form onSubmit={onSubmit} class="flex flex-col gap-4">
        <Field label="Titel" htmlFor="npt-title" required>
          <input
            id="npt-title"
            type="text"
            value={title}
            onInput={(e) => setTitle((e.target as HTMLInputElement).value)}
            required
            maxLength={500}
            autoFocus
            class={INPUT_CLS}
            placeholder="z.B. Angebot nachfassen"
          />
        </Field>

        {/* Ablage chooser — Persönlich or a writable Space */}
        <Field label="Ablage" htmlFor="npt-ablage">
          <select
            id="npt-ablage"
            value={ablage}
            onChange={(e) => setAblage((e.target as HTMLSelectElement).value)}
            class={INPUT_CLS}
          >
            <option value={PERSONAL}>Persönlich</option>
            {writableSpaces.map((s) => (
              <option key={s.id} value={String(s.id)}>
                {s.name}
              </option>
            ))}
          </select>
        </Field>

        {/* Personal list picker — only when "Persönlich" is selected */}
        {ablage === PERSONAL && (
          <Field label="Liste" htmlFor="npt-list">
            <select
              id="npt-list"
              value={personalListId !== undefined ? String(personalListId) : ""}
              onChange={(e) => {
                const v = (e.target as HTMLSelectElement).value;
                setPersonalListId(v ? Number(v) : undefined);
              }}
              class={INPUT_CLS}
            >
              {/* Kein „Ohne Liste" mehr — nur der Ladezustand braucht noch
                  einen leeren Eintrag, sonst zeigte das Feld die erste Liste
                  an, ohne sie ausgewählt zu haben. */}
              {personalListId === undefined && <option value="">—</option>}
              {personalLists.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
          </Field>
        )}

        {/* Assignee — only when a Space is selected. The field stays visible even
            when nobody is assignable so the user understands the feature exists
            and why the list is empty (no department granted to this space). */}
        {ablage !== PERSONAL && (
          <Field label="Zuständig">
            {spaceMembers.length > 0 ? (
              <AssigneePicker
                value={assigneeIds}
                candidateIds={spaceMembers.map((m) => m.user_id)}
                label="Zuständige"
                onChange={setAssigneeIds}
              />
            ) : (
              <p class="text-sm text-[color:var(--color-on-surface-variant)]">
                Keine zuweisbaren Personen. Weise dem Space eine Abteilung zu –
                z.&nbsp;B. „Gesamtorganisation" für alle (Admin → Spaces →
                Abteilungen).
              </p>
            )}
          </Field>
        )}

        <Field label="Fällig am" htmlFor="npt-due">
          <input
            id="npt-due"
            type="date"
            value={dueDate}
            onInput={(e) => setDueDate((e.target as HTMLInputElement).value)}
            class={INPUT_CLS}
          />
        </Field>

        <Field label="Priorität" htmlFor="npt-priority">
          <select
            id="npt-priority"
            value={priority}
            onChange={(e) =>
              setPriority((e.target as HTMLSelectElement).value as Priority)
            }
            class={INPUT_CLS}
          >
            <option value="low">Niedrig</option>
            <option value="medium">Mittel</option>
            <option value="high">Hoch</option>
            <option value="urgent">Dringend</option>
          </select>
        </Field>

        <div class="flex items-center justify-end gap-2 pt-2 border-t border-[color:var(--color-outline-variant)] -mx-5 -mb-4 px-5 pb-4 mt-2">
          <button
            type="button"
            onClick={onClose}
            class="rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-4 py-2 text-sm font-medium text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors min-h-[32px]"
          >
            Abbrechen
          </button>
          <Button
            variant="primary"
            size="lg"
            type="submit"
            disabled={submitting || title.trim() === ""}
          >
            {submitting ? "Lege an…" : "Anlegen"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
